// src/lib/vorWartung.js
// --- WERTE DER LETZTEN WARTUNG -------------------------------------------------
// Anna-Lena 15.09.2026 (Vorschlag 7): Beim Pruefen soll der Monteur sehen, was
// bei der letzten Wartung dieser Station herauskam - "letztes Mal n.i.O. –
// fehlt", "letztes Mal 0,12 Ω". Nur ein HINWEIS: Es wird nie etwas
// uebernommen oder vorausgefuellt.
//
// Geladen wird das letzte abgeschlossene Protokoll derselben Station (vor dem
// aktuellen Wartungsdatum). Das Ergebnis liegt zusaetzlich im Geraet
// ("etb-vorwartung-<protokollId>"), damit es auch ohne Netz da ist.

import { createContext, useContext, useEffect, useState } from 'react';
import { supabase, withTimeout } from './supabase';
import { ergebnisUndHinweis } from './pruefpunktLogik';

const CACHE = 'etb-vorwartung-';
const CACHE_LISTE = CACHE + 'liste';
const CACHE_ANZAHL = 12;
const FERTIG = ['monteur_abgeschlossen', 'buero_freigegeben', 'abgeschlossen'];

export const VorWartungContext = createContext(null);
export function useVorWartung() { return useContext(VorWartungContext); }

// Schluessel eines Pruefpunkts: Kapitel-Code, Bezug (Trafo-ID) und Position.
export function vorWartungSchluessel(modulCode, bezugsId, pos) {
  return (modulCode || '') + '|' + (bezugsId || '') + '|' + (pos || '');
}

function cacheLesen(protokollId) {
  try { return JSON.parse(localStorage.getItem(CACHE + protokollId) || 'null'); } catch { return null; }
}
function cacheSchreiben(protokollId, daten) {
  try {
    let liste = [];
    try { liste = JSON.parse(localStorage.getItem(CACHE_LISTE) || '[]'); } catch { liste = []; }
    if (!Array.isArray(liste)) liste = [];
    liste = [protokollId, ...liste.filter(x => x !== protokollId)];
    for (const alt of liste.slice(CACHE_ANZAHL)) localStorage.removeItem(CACHE + alt);
    localStorage.setItem(CACHE_LISTE, JSON.stringify(liste.slice(0, CACHE_ANZAHL)));
    localStorage.setItem(CACHE + protokollId, JSON.stringify(daten));
  } catch { /* Speicher voll - dann nur ohne Netz nicht da */ }
}

// Laedt die Werte der letzten Wartung. Ergebnis:
//   { datum, punkte: { schluessel: { e, h, nio } }, erder: { anschlussId: { ohm, ol } } }
// oder { datum: null } wenn es keine fruehere Wartung gibt.
export async function ladeVorWartung(protokoll) {
  if (!protokoll?.id || !protokoll?.station_id) return null;
  let q = supabase.from('protokolle').select('id, wartungs_datum')
    .eq('station_id', protokoll.station_id).neq('id', protokoll.id).in('status', FERTIG)
    .order('wartungs_datum', { ascending: false, nullsFirst: false }).limit(1);
  if (protokoll.wartungs_datum) q = q.lt('wartungs_datum', protokoll.wartungs_datum);
  const { data: vorher, error: e1 } = await withTimeout(q, 20000, 'Letzte Wartung suchen');
  if (e1) throw e1;
  const alt = (vorher || [])[0];
  if (!alt) return { datum: null, punkte: {}, erder: {} };

  const { data: mods, error: e2 } = await withTimeout(
    supabase.from('protokoll_module').select('id, bezugs_id, modul_vorlagen(code)').eq('protokoll_id', alt.id),
    20000, 'Letzte Wartung laden');
  if (e2) throw e2;
  const modNachId = new Map((mods || []).map(m => [m.id, m]));
  const punkte = {};
  if (modNachId.size > 0) {
    const { data: pps, error: e3 } = await withTimeout(
      supabase.from('protokoll_modul_pruefpunkte')
        .select('protokoll_modul_id, pos_nummer, typ, einheit, wert_auswahl, wert_zahl, wert_bool, wert_text, wert_modifiers, in_ordnung, bemerkung, zusatz_werte, zusatz_felder')
        .in('protokoll_modul_id', [...modNachId.keys()]),
      30000, 'Letzte Wartung laden');
    if (e3) throw e3;
    for (const pp of pps || []) {
      const { ergebnis, hinweis } = ergebnisUndHinweis(pp);
      if (!ergebnis && !hinweis) continue;
      const m = modNachId.get(pp.protokoll_modul_id);
      punkte[vorWartungSchluessel(m?.modul_vorlagen?.code, m?.bezugs_id, pp.pos_nummer)] =
        { e: ergebnis, h: hinweis, nio: pp.in_ordnung === false };
    }
  }

  const { data: mess, error: e4 } = await withTimeout(
    supabase.from('protokoll_erderanlage_messungen').select('anschluss_id, ohm_wert, ueber_messbereich').eq('protokoll_id', alt.id),
    20000, 'Letzte Messwerte laden');
  if (e4) throw e4;
  const erder = {};
  for (const m of mess || []) {
    if (m.ueber_messbereich || m.ohm_wert != null) erder[m.anschluss_id] = { ohm: m.ohm_wert, ol: !!m.ueber_messbereich };
  }
  return { datum: alt.wartungs_datum, punkte, erder };
}

// Fuer die Protokollseite: sofort aus dem Geraet, dann frisch vom Server.
export function useVorWartungLaden(protokoll) {
  const id = protokoll?.id;
  const [daten, setDaten] = useState(() => (id ? cacheLesen(id) : null));
  useEffect(() => {
    if (!id || !protokoll?.station_id) return undefined;
    let abgebrochen = false;
    setDaten(cacheLesen(id));
    ladeVorWartung(protokoll)
      .then(d => { if (!abgebrochen && d) { setDaten(d); cacheSchreiben(id, d); } })
      .catch(() => { /* ohne Netz bleibt der gemerkte Stand */ });
    return () => { abgebrochen = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, protokoll?.station_id, protokoll?.wartungs_datum]);
  return daten;
}

// Fuer das Vorab-Laden (Vorschlag 11).
export async function vorWartungVorladen(protokoll) {
  const d = await ladeVorWartung(protokoll);
  if (d) cacheSchreiben(protokoll.id, d);
  return d;
}

export function datumKurz(d) {
  const m = String(d || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? m[3] + '.' + m[2] + '.' + m[1] : '';
}
